import { Injectable , NgZone  } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject,Observable, Subject, tap } from 'rxjs';
import { AuditListDownloadSSE, CampaignAuditHistory, CampaignDetails, CampaignListSorting, CommunicationHistorySearchParams, CommunicationDetail, SmsCommunicationDetail, DownloadAuditList, GetTestGroupsResponse, Languages, NodeLabelUpdate, SendTestGroupPayload, SendTestGroupResponse, SendTestGroupType, smsAdmin } from '../model/campaign.interface';
import { uiClientsConfig } from 'admin-ui-v2-config';
import { data, map } from 'jquery';
import { ToastrService } from 'ngx-toastr';


interface testEmailResponse {
  isActionSuccess: boolean,
  data: string,
  validationErrors: string[]
}

@Injectable({
  providedIn: 'root'
})

export class CampaignService {

  constructor(private http: HttpClient,private zone: NgZone ,private toastr: ToastrService ){ }

  /** Languages configured on the brand; filled by the email tab once loaded (used for save-time validation). */
  brandLanguageNames: string[] = [];
  isKnownBrandLanguage(name: string | null | undefined): boolean {
    if (!this.brandLanguageNames.length) { return true; } // list not loaded: do not block
    const wanted = (name ?? 'English').trim().toLowerCase();
    return this.brandLanguageNames.some((n) => n.trim().toLowerCase() === wanted);
  }

  sendCampaignMailsApi: string = "api/v1/platform/send-campaign-mails";
  sendCampaignSMSApi: string = "api/v1/platform/twilio-sms-campaign/";
  marketingAutomationCampaignApi: string = "api/v1/platform/marketing-automation-campaign";
  brandManagementApi: string = "api/v1/platform/brand-management/";
  unlayerTokenApi:string = "api/v1/platform/unlayer-editor-token";
  sendEmailCampaignApi: string = "api/v1/platform/send-campaign-mails/campaign-control";
  smsRegulatoryMMessageApi: string = "api/v1/platform/brand-sms-regulatary-message";
  patientListArchiveOrRestoreApi: string = "api/v1/platform/patient-file-upload/update-archive-state";
  subdomain: string = window.location.hostname.split("-")[0];
  codeCategoryValueLookupApi: string = "api/v1/platform/code-value-by-category-lookup/";
  campaignAuditListDownloadApi: string = "api/v1/platform/marketing-automation-campaign/campaign-audit/generate-report";
  campaignAuditHistoryApi: string = "/api/v1/platform/patient-file-upload/audit-history";
  private languagesList: Languages[] = [
    { code: 'en', name: 'English', default: true },
    { code: 'es', name: 'Spanish', default: false },
    { code: 'ru', name: 'Russian', default: false },
  ];

  activeCommunicationId = new BehaviorSubject<string>('');
  activeCommunicationId$ = this.activeCommunicationId.asObservable();

  activeTab = new BehaviorSubject<number>(0);
  activeTab$ = this.activeTab.asObservable();

  activePageStandard = new BehaviorSubject<number>(1);
  activePageStandard$ = this.activePageStandard.asObservable();

  activePageArchive = new BehaviorSubject<number>(1);
  activePageArchive$ = this.activePageArchive.asObservable();

  private eventSource: EventSource | null = null;
  standardListSorting = new BehaviorSubject<CampaignListSorting>({
    sortOrder : 'DESC',
    sortBy : 'lastModifiedDateTimeUtc',
    columnName : '',
  });
  standardListSorting$ = this.standardListSorting.asObservable();

  archiveListSorting = new BehaviorSubject<CampaignListSorting>({
    sortOrder : 'DESC',
    sortBy : 'lastModifiedDateTimeUtc',
    columnName : '',
  });
  archiveListSorting$ = this.archiveListSorting.asObservable();



  private languagesSubject = new BehaviorSubject<Languages[]>(this.languagesList);
  getAllLanguages$: Observable<Languages[]> = this.languagesSubject.asObservable();
  private fileUploadProcessEnded = new BehaviorSubject<boolean>(false)
  isFileUploadEnded = this.fileUploadProcessEnded.asObservable()
  changeFileUploadStatus(modalClosed: boolean) {
      this.fileUploadProcessEnded.next(modalClosed)
  }


  readonly nodeLabelUpdate$ = new Subject<NodeLabelUpdate>();

  readonly fitNetwork$ = new Subject<void>();

  updateNodeLabel(stateId: string, label: string): void {
    this.nodeLabelUpdate$.next({ stateId, label });
  }

  getCodeCategoryValues(codeCategoryId: string): Observable<any> {
    return this.http.get<any>(this.codeCategoryValueLookupApi + `/${codeCategoryId}`)
  }

  triggerFit(): void {
    this.fitNetwork$.next();
  }


  sendTestMail(incomingObject: any): Observable<testEmailResponse> {
    let obj = {
      subject: incomingObject.subject ? incomingObject.subject : "Testing, Testing - One, Two, Three",
      emailDisplayName: incomingObject.emailDisplayName,
      emailPreviewText: incomingObject.emailPreviewText,
      campaignId: incomingObject.campaignId,
      content: incomingObject.html,
      toEmailAddress: incomingObject.emailList,
      fromEmailAddress: incomingObject.fromEmailAddress,
      replyToEmailAddress: incomingObject.replyToEmailAddress,
      isTestModeEnabled: true
    }
    return this.http.post<any>(this.sendCampaignMailsApi, obj);
  }

  createTestGroup(payload: SendTestGroupPayload): Observable<SendTestGroupResponse> {
    return this.http.post<SendTestGroupResponse>(this.marketingAutomationCampaignApi + '/send-test-group', payload);
  }

  getTestGroups(type: SendTestGroupType, search: string = '', pageNumber: number = 1, limit: number = 100): Observable<GetTestGroupsResponse> {
    const params: { [key: string]: string | number } = { pageNumber, limit };
    if (search) {
      params['search'] = search;
    }
    return this.http.get<GetTestGroupsResponse>(`${this.marketingAutomationCampaignApi}/send-test-group/${type}`, { params });
  }

  updateTestGroup(id: number, payload: SendTestGroupPayload): Observable<SendTestGroupResponse> {
    return this.http.put<SendTestGroupResponse>(`${this.marketingAutomationCampaignApi}/send-test-group/${id}`, payload);
  }

  deleteTestGroup(id: number): Observable<SendTestGroupResponse> {
    return this.http.delete<SendTestGroupResponse>(`${this.marketingAutomationCampaignApi}/send-test-group/${id}`);
  }

  saveCampaign(incomingObject: CampaignDetails, status: string, auditHistory?: CampaignAuditHistory | null, transitionalStateFlag?: boolean): Observable<any> {
    let payLoad = {
      campaignName: incomingObject.campaignName.trim(),
      description: incomingObject.description,
      emailSubject: incomingObject.emailSubject,
      emailDisplayName: incomingObject.emailDisplayName,
      emailPreviewText: incomingObject.emailPreviewText,
      selectedLanguage: incomingObject.selectedLanguage,
      replyToEmailAddress: incomingObject.replyToEmailAddress,
      fromEmailAddress: incomingObject.fromEmailAddress,
      selectedLists: incomingObject.selectedLists,
      emailContent: incomingObject.emailContent,
      settings: incomingObject.settings,
      smsContent: incomingObject.smsContent,
      voiceContent: incomingObject.voiceContent,
      selectedChannels:incomingObject.selectedChannels,
      oldChannels:incomingObject.oldChannels,
      status: status,
      isReportingDisabled : incomingObject.isReportingDisabled ? 1 : 0,
      usingCampaignWorkflow: incomingObject.usingCampaignWorkflow,
      workflowData: incomingObject.workflowData,
      workflowCommunications: incomingObject.workflowCommunications,
      auditHistory: auditHistory ?? null,
      transitionalStateFlag: transitionalStateFlag ?? false
    }
    return this.http.post<any>(this.marketingAutomationCampaignApi, payLoad);
  }

  updateCampaign(incomingObject: CampaignDetails, id:string, status: string, auditHistory?: CampaignAuditHistory | null, transitionalStateFlag?: boolean): Observable<any> {
    let payLoad = {
      campaignName: incomingObject.campaignName.trim(),
      description: incomingObject.description,
      emailSubject: incomingObject.emailSubject,
      emailDisplayName: incomingObject.emailDisplayName,
      emailPreviewText: incomingObject.emailPreviewText,
      selectedLanguage: incomingObject.selectedLanguage,
      replyToEmailAddress: incomingObject.replyToEmailAddress,
      fromEmailAddress: incomingObject.fromEmailAddress,
      selectedLists: incomingObject.selectedLists,
      emailContent: incomingObject.emailContent,
      settings: incomingObject.settings,
      smsContent: incomingObject.smsContent,
      voiceContent: incomingObject.voiceContent,
      selectedChannels:incomingObject.selectedChannels,
      status: status,
      isArchived : incomingObject.isArchived ? 1 : 0,
      isReportingDisabled : incomingObject.isReportingDisabled ? 1 : 0,
      usingCampaignWorkflow: incomingObject.usingCampaignWorkflow,
      workflowData: incomingObject.workflowData,
      workflowCommunications: incomingObject.workflowCommunications,
      auditHistory: auditHistory ?? null,
      transitionalStateFlag: transitionalStateFlag ?? false
    }

    return this.http.put<any>(this.marketingAutomationCampaignApi + `/${id}`, payLoad);
  }

  copyCampaign(incomingObject: any, auditHistory?: CampaignAuditHistory | null): Observable<any> {
    let payLoad = {
      campaignName: incomingObject.campaignName,
      description: incomingObject.description,
      emailSubject: incomingObject.emailSubject,
      emailDisplayName: incomingObject.emailDisplayName,
      emailPreviewText: incomingObject.emailPreviewText,
      selectedLanguage: incomingObject.selectedLanguage,
      replyToEmailAddress: incomingObject.replyToEmailAddress,
      fromEmailAddress: incomingObject.fromEmailAddress,
      selectedLists: incomingObject.selectedLists,
      emailContent: incomingObject.emailContent,
      settings: incomingObject.settings,
      selectedChannels: incomingObject.selectedChannels,
      oldChannels: incomingObject.oldChannels,
      smsContent: incomingObject.smsContent,
      voiceContent: incomingObject.voiceContent,
      status: 'DRAFT',
      isArchived : 0,
      isReportingDisabled : incomingObject.isReportingDisabled,
      usingCampaignWorkflow: incomingObject.usingCampaignWorkflow,
      workflowData: incomingObject.workflowData,
      workflowCommunications: incomingObject.workflowCommunications,
      auditHistory: auditHistory ?? null
    }
    return this.http.post<any>(this.marketingAutomationCampaignApi + '/copy-campaign', payLoad);
  }


  getAllCampaigns(page: number, sortBy: string, sortOrder: string, limit: number, search : string, statusFilter: string[] | string = []): Observable<any> {
    const obj: any = { "limit": limit, "pageNumber": page, "sortBy": sortBy, "sortOrder": sortOrder,  "isArchiveList" : 0 , "search": search? search: ''};
    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      obj.status = statusFilter.join(',');
    } else if (typeof statusFilter === 'string' && statusFilter.length > 0) {
      obj.status = statusFilter;
    }
    return this.http.get<any>(this.marketingAutomationCampaignApi, { params: obj })
  }

  getAllArchivedCampaigns(page: number, sortBy: string, sortOrder: string, limit: number, search : string, statusFilter: string[] | string = []): Observable<any> {
    const obj: any = { "limit": limit, "pageNumber": page, "sortBy": sortBy, "sortOrder": sortOrder, "isArchiveList" : 1, "search": search? search: ''};
    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      obj.status = statusFilter.join(',');
    } else if (typeof statusFilter === 'string' && statusFilter.length > 0) {
      obj.status = statusFilter;
    }
    return this.http.get<any>(this.marketingAutomationCampaignApi, { params: obj })
  }


  // prospect-lists/get-lists?pageNumber=1&limit=2&sortBy=totalRows&sortOrder=ASC

  getAllProspectsLists(page: number, sortBy: string, sortOrder: string, limit: number, search: string, campaignId: string = '', status: string = '', isArchivedView: boolean = false): Observable<any> {
    const obj: any = { pageNumber: page, limit, sortBy, sortOrder, search: search ? search : '', campaignId, isArchivedView };
    if (status) { obj.status = status; }
    return this.http.get<any>(this.marketingAutomationCampaignApi + "/prospect-lists/get-lists/", { params: obj })
  }

  archiveProspectLists(listIds: string[]): Observable<any> {
    return this.http.put<any>(this.patientListArchiveOrRestoreApi, { ids: listIds, action: 'archive' });
  }

  restoreProspectLists(listIds: string[]): Observable<any> {
    return this.http.put<any>(this.patientListArchiveOrRestoreApi, { ids: listIds, action: 'restore' });
  }

  getAllFromEmails(): Observable<any> {
    return this.http.get<any>(this.marketingAutomationCampaignApi + "/from-emails-list/from-emails")
  }

  getList(id:string, limit:number, page:number): Observable<any> {
    const obj = {"pageNumber": page, "limit": limit };

    return this.http.get<any>(this.marketingAutomationCampaignApi + `/prospect-lists/get-prospects/${id}`, { params: obj })
  }

  getListUpdatedStatus(ids:string): Observable<any> {
    const obj = {"ids": ids };

    return this.http.get<any>(this.marketingAutomationCampaignApi + `/campaign-status`, { params: obj })
  }

  getCampaignById(id:string){
    return this.http.get<any>(this.marketingAutomationCampaignApi + `/${id}`)
  }

  generateUnlayerToken(){
    return this.http.get<any>(this.unlayerTokenApi + '/')
  }
  getSmsRegulatoryMessage(){
    return this.http.get<any>(this.smsRegulatoryMMessageApi + '/')
  }
  updateSmsRegulatoryMessage(payload:smsAdmin){
    return this.http.put<any>(this.smsRegulatoryMMessageApi + '/',payload)
  }

  changeCampaignStatus(id:string, status: string): Observable<any> {
    const obj = {id: id, status: status}
    return this.http.post<any>(this.sendEmailCampaignApi + `/${id}/${status}`, {params: obj} )
  }

  public updateAllowedLanguages(allowedLanguages: { [code: string]: boolean }): void {
    // Filter the languages list to only include languages that are allowed.
    const filteredLanguages = this.languagesList.filter(language =>language.code === 'en' || allowedLanguages[language.code] === true);
    this.languagesSubject.next(filteredLanguages);
  }

  sendTest(incomingObject: any, type:string): Observable< HttpResponse<any>> {
    let subURL = '';
    if(type ==='SMS' || type === 'MMS'){
      subURL = "/send-sms/"
    }
    if(type ==='IVR'){
      subURL = "/send-ivr/"
    }
    return this.http.post<any>(this.sendCampaignSMSApi+uiClientsConfig.customerUrlPrefix+subURL, incomingObject,  { observe: 'response' });
  }

  validateEmail(email:string) {
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  validatePhoneNumber(phoneNumber: string): boolean {
    const re = /^(\d{10}|\+1\d{10})$/;
    return re.test(String(phoneNumber));
  }

  searchCommunicationHistory(params: CommunicationHistorySearchParams): Observable<any> {
    const obj: any = {
      channel: params.channel === 'EMAIL' ? 'Email' : 'SMS',
      contact: params.contact,
      sortOrder: params.sortOrder ?? 'DESC',
      limit: params.limit,
    };
    if (params.cursor) { obj.cursor = params.cursor; }
    if (params.campaignName) { obj.campaignName = params.campaignName; }
    if (params.listName) { obj.listName = params.listName; }
    return this.http.get<any>('api/v1/platform/campaign-history/', { params: obj });
  }

  getCommunicationDetail(campaignTaskId: string): Observable<CommunicationDetail> {
    return this.http.get<CommunicationDetail>(`api/v1/platform/campaign-history/${campaignTaskId}`);
  }

  getSmsCommunicationDetail(campaignTaskId: string): Observable<SmsCommunicationDetail> {
    return this.http.get<SmsCommunicationDetail>(`api/v1/platform/campaign-history/sms/${campaignTaskId}`);
  }

  startFileDownload(params: DownloadAuditList): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.eventSource) {
        observer.error(new Error('Export is already in progress.'));
      }
      window.addEventListener('beforeunload', this.leavingPage);
      const query = new URLSearchParams(params as any).toString();
      const url = `${this.campaignAuditListDownloadApi}?${query}`;
      this.eventSource = new EventSource(url, { withCredentials: true });

      let preparingToast: any;
      this.eventSource.addEventListener('started', () => {
        this.zone.run(() => {
         preparingToast = this.toastr.success('Preparing your file for download…');
        });
      });

      this.eventSource.addEventListener('completed', (event) => {
        const data: AuditListDownloadSSE  = JSON.parse(event.data);
        this.zone.run(() => {
          try {
            if (preparingToast) {
              this.toastr.remove(preparingToast.toastId);
            }
            this.downloadCSVByUrl(data.url, data.fileName);
            this.closeFileDownloadSSE();
            observer.next();
            observer.complete();
          } catch (error) {
            this.closeFileDownloadSSE();
            observer.error(new Error('Failed to process export file download.'));
          }
        });
      });

      this.eventSource.addEventListener('failed', () => {
        this.zone.run(() => {
          if (preparingToast) {
            this.toastr.remove(preparingToast.toastId);
          }
          this.closeFileDownloadSSE();
          this.toastr.error(
            'Failed to download the file. Please try again later.',
          );
          observer.error(new Error('Failed to download the file. Please try again later.'));
        });
      });

      this.eventSource.onerror = () => {
        this.zone.run(() => {
          if (this.eventSource && (this.eventSource.readyState === EventSource.CLOSED || this.eventSource.readyState === EventSource.CONNECTING)) {
            this.eventSource.close();
            this.eventSource = null;
            window.removeEventListener('beforeunload', this.leavingPage);
            observer.error(new Error('Unable to complete export. Please try again.'));
          }
        });
      };

      return () => {
        this.closeFileDownloadSSE();
      };
    });
  }

  closeFileDownloadSSE() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    window.removeEventListener('beforeunload', this.leavingPage);
  }
  downloadCSVByUrl(fileUrl: string, fileName: string): void {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.target = '_blank';
    a.click();
  }

  leavingPage(event): any {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }

  getAuditHistory(campaignId: string, pageNumber: number, limit: number): Observable<any> {
    const params = new URLSearchParams({ pageNumber: String(pageNumber), limit: String(limit) }).toString();
    return this.http.get(`${this.marketingAutomationCampaignApi}/${campaignId}/audit-history?${params}`);
  }

  getGeneralAuditHistory(pageNumber: number, limit: number): Observable<any> {
    const params = new URLSearchParams({ page: String(pageNumber), limit: String(limit) }).toString();
    return this.http.get(`${this.campaignAuditHistoryApi}?${params}`);
  }

}
